import { getItemOrNull, getMe } from './abs';
import { AbsError } from './client';
import { FINISHED_THRESHOLD, isAudiobookItem, mapWithConcurrency, parseEpochMs, progressIsFinished, progressItemIds } from './helpers';
import { setProgressFromUser } from './progressStore';
import type { AbsUser, LibraryItem, MediaProgress } from './types';

// "Continue Listening" and "Finished" lists built from the user's progress
// (`GET /api/me` → `mediaProgress`), hydrated item by item like Flutter's
// AbsUserProgressApiService (lib/services/abs_user_progress_api_service.dart:16-231).
// They span every library, as in Flutter.

type ProgressEntry = MediaProgress & Record<string, unknown>;

/** `mediaProgress` from `/api/me` (or `user.mediaProgress`), podcast episodes excluded. */
export function mediaProgressOf(user: AbsUser | { user?: AbsUser } | null | undefined): ProgressEntry[] {
  const root = (user ?? {}) as AbsUser & { user?: AbsUser };
  const list = root.mediaProgress ?? root.user?.mediaProgress;
  if (!Array.isArray(list)) return [];
  return list.filter((p): p is ProgressEntry => !!p && typeof p === 'object' && !p.episodeId) as ProgressEntry[];
}

/** Finished entries, newest `finishedAt` first. */
export function finishedEntries(progress: readonly ProgressEntry[]): ProgressEntry[] {
  return progress
    .filter((p) => progressIsFinished(p))
    .sort((a, b) => (parseEpochMs(b.finishedAt) ?? 0) - (parseEpochMs(a.finishedAt) ?? 0));
}

/** The entry sits in the finished zone (≥ 98 % by `progress` or position) even without the finished flag. */
export function progressAtEnd(p: Partial<MediaProgress>): boolean {
  const duration = Number(p.duration) || 0;
  const ratio = duration > 0 ? (Number(p.currentTime) || 0) / duration : 0;
  return Math.max(ratio, Number(p.progress) || 0) >= FINISHED_THRESHOLD;
}

/**
 * Started but unfinished entries, most recently updated first. Entries at the
 * end of the book count as finished even when ABS never set the flag. Entries
 * the user hid from Continue Listening in ABS are left out (Flutter ignores
 * the flag; see data/README.md "Deviations").
 */
export function inProgressEntries(progress: readonly ProgressEntry[], opts: { includeHidden?: boolean } = {}): ProgressEntry[] {
  return progress
    .filter((p) => !progressIsFinished(p) && !progressAtEnd(p) && (p.currentTime ?? 0) > 0 && (opts.includeHidden || !p.hideFromContinueListening))
    .sort((a, b) => (b.lastUpdate ?? 0) - (a.lastUpdate ?? 0));
}

/** Finished item ids (Flutter `getFinishedBookIds`), keyed by every id alias. */
export function finishedBookIds(progress: readonly ProgressEntry[]): Set<string> {
  const ids = new Set<string>();
  for (const p of progress) if (progressIsFinished(p)) progressItemIds(p).forEach((id) => ids.add(id));
  return ids;
}

export interface ProgressListOptions {
  /** 0 = everything. */
  limit?: number;
  page?: number;
  /** Item fetches in flight at once (Flutter fetches one at a time). */
  concurrency?: number;
  /** Reuse progress the caller already has instead of calling `/api/me`. */
  progress?: readonly ProgressEntry[];
}

function slice<T>(list: T[], limit: number, page: number): T[] {
  if (limit <= 0) return list;
  const start = page * limit;
  return start >= list.length ? [] : list.slice(start, start + limit);
}

async function loadProgress(opts: ProgressListOptions): Promise<ProgressEntry[]> {
  if (opts.progress) return [...opts.progress];
  const user = await getMe();
  setProgressFromUser(user);
  return mediaProgressOf(user);
}

/**
 * Some progress entries could not be hydrated (network error, 5xx, expired
 * session). `items` holds the ones that loaded; callers keep what they showed
 * before instead of replacing it with a shorter list.
 */
export class ProgressHydrationError extends Error {
  constructor(
    /** The first failure. */
    readonly error: unknown,
    readonly items: LibraryItem[],
    readonly failures: number,
  ) {
    super(error instanceof Error && error.message ? error.message : 'Could not load your listening progress.');
    this.name = 'ProgressHydrationError';
  }
}

/** The item is gone or not visible to this user (404 / 403): skip it like Flutter. */
const isUnavailable = (err: unknown): boolean => err instanceof AbsError && (err.status === 404 || err.status === 403);

/**
 * Fetches the item behind each progress entry, trying every id alias (404 /
 * 403 → next alias), dropping podcasts, and attaching the entry as
 * `userMediaProgress`. Any other failure rejects with `ProgressHydrationError`.
 */
async function hydrate(entries: ProgressEntry[], concurrency: number): Promise<LibraryItem[]> {
  let failures = 0;
  let firstError: unknown;
  const items = await mapWithConcurrency(entries, concurrency, async (entry) => {
    for (const id of progressItemIds(entry)) {
      try {
        const item = await getItemOrNull(id, { expanded: false });
        if (!item) continue;
        if (!isAudiobookItem(item)) return null;
        return { ...item, userMediaProgress: entry } as LibraryItem;
      } catch (err) {
        if (isUnavailable(err)) continue;
        failures++;
        firstError ??= err;
        return null;
      }
    }
    return null;
  });
  const found = items.filter((x): x is LibraryItem => x !== null);
  if (failures > 0) throw new ProgressHydrationError(firstError, found, failures);
  return found;
}

/** Hydrates progress entries into items in order (see `getBooksInProgress`); for callers with their own entry list. */
export function hydrateProgressEntries(entries: readonly ProgressEntry[], concurrency = 4): Promise<LibraryItem[]> {
  return hydrate([...entries], concurrency);
}

/** Finished books, newest `finishedAt` first (Flutter `getFinishedBooks`). Not library-scoped. */
export async function getFinishedBooks(opts: ProgressListOptions = {}): Promise<LibraryItem[]> {
  const entries = slice(finishedEntries(await loadProgress(opts)), opts.limit ?? 0, opts.page ?? 0);
  const items = await hydrate(entries, opts.concurrency ?? 4);
  return items.sort((a, b) => (parseEpochMs(b.userMediaProgress?.finishedAt) ?? 0) - (parseEpochMs(a.userMediaProgress?.finishedAt) ?? 0));
}

/** Books in progress, most recently listened first (Flutter `getBooksInProgress`). Not library-scoped. */
export async function getBooksInProgress(opts: ProgressListOptions & { includeHidden?: boolean } = {}): Promise<LibraryItem[]> {
  const entries = slice(inProgressEntries(await loadProgress(opts), opts), opts.limit ?? 0, opts.page ?? 0);
  return hydrate(entries, opts.concurrency ?? 4);
}
